import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  RHFAutocomplete,
  RHFDatePicker,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useLocation, useNavigate } from '@tyro/router/hooks';
import type { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { ReturnTypeUseMailSearch } from '../../api/mail-search';
import { useMailSearchProps } from '../../hooks/use-mail-search-props';

export interface MailAdvancedSearchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isSchoolMail: boolean;
  isRulesMail: boolean;
}

type FormState = {
  to?: Dayjs;
  from?: Dayjs;
  recipients?: ReturnTypeUseMailSearch[];
  senders?: ReturnTypeUseMailSearch[];
};

function getSearchParamsForAdvancedSearch(data: FormState) {
  const searchParams = new URLSearchParams();

  if (data.senders?.length) {
    searchParams.append(
      'senders',
      data.senders.map((sender) => sender.partyId).join(','),
    );
  }

  if (data.recipients?.length) {
    searchParams.append(
      'recipients',
      data.recipients.map((recipient) => recipient.partyId).join(','),
    );
  }

  if (data.from) {
    searchParams.append('from', data.from.format('YYYY-MM-DD'));
  }

  if (data.to) {
    searchParams.append('to', data.to.format('YYYY-MM-DD'));
  }

  return searchParams.toString();
}

export function MailAdvancedSearchDrawer({
  isOpen,
  onClose,
  isSchoolMail,
  isRulesMail,
}: MailAdvancedSearchDrawerProps) {
  const { t } = useTranslation(['common', 'mail']);
  const location = useLocation();
  const navigate = useNavigate();

  const toMailSearchProps = useMailSearchProps();
  const fromMailSearchProps = useMailSearchProps();

  const { control, handleSubmit } = useForm<FormState>();

  let searchPathName = '/mail/search';
  if (isSchoolMail) {
    searchPathName = '/mail/school/search';
  } else if (isRulesMail) {
    searchPathName = '/mail/rules/search';
  }

  const onApply = handleSubmit((data) => {
    navigate({
      pathname: searchPathName,
      search: getSearchParamsForAdvancedSearch(data),
    });
    onClose();
  });

  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location, onClose]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      hideBackdrop
      disableEnforceFocus
      sx={{
        '& .MuiPaper-root': {
          width: '100%',
          maxWidth: 320,
        },
      }}
    >
      <DialogTitle
        onClose={onClose}
        containerProps={{
          sx: { borderBottom: '1px solid', borderBottomColor: 'divider' },
        }}
      >
        {t('common:advancedFilter')}
      </DialogTitle>
      <DialogContent>
        <Stack py={3} spacing={3}>
          <RHFAutocomplete<FormState, ReturnTypeUseMailSearch, true>
            label={t('mail:placeholders.to')}
            controlProps={{
              control,
              name: 'recipients',
            }}
            {...fromMailSearchProps}
          />
          <RHFAutocomplete<FormState, ReturnTypeUseMailSearch, true>
            label={t('mail:placeholders.from')}
            controlProps={{
              control,
              name: 'senders',
            }}
            {...toMailSearchProps}
          />
          <RHFDatePicker
            label={t('mail:sentAfterOnDate')}
            controlProps={{
              control,
              name: 'from',
            }}
          />
          <RHFDatePicker
            label={t('mail:sentBeforeOnDate')}
            controlProps={{
              control,
              name: 'to',
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ p: 3, borderTop: '1px solid', borderTopColor: 'divider' }}
      >
        <Button variant="contained" onClick={onApply}>
          {t('common:actions.apply')}
        </Button>
      </DialogActions>
    </Drawer>
  );
}
